import getPageName from "../getPageName";
import getDataProgram from "./getDataProgram";
import { disableProgramField, enableProgramField } from "./programFieldHint";

class ProgramDropdownManager {
  // Скрытие программ на главной странице в форме
  static EXCLUDED_PROGRAMS = new Set([
    "ai-profession",
    "ai-start",
    "ai-start-fb",
    "ai-start-tg",
    "promptengineertg",
    "marketplacetg",
    "new-marketplace",
    "new-graphicdesign",
    "new-developer1c",
  ]);

  static PROGRAM_ATTRIBUTES = ["operator", "program_id"];
  static SELECTOR = {
    programSelect: "ProgramHome",
    regionSelects: 'select[name="Region"]',
    hiddenFields: ".program-hidden",
    dateFields: ".date-select",
    cohortFields: ".cohort-id-hidden",
  };

  constructor(requestPrograms) {
    this.requestPrograms = requestPrograms;
    this.programSelect = document.getElementById(
      this.constructor.SELECTOR.programSelect,
    );
    this.regionSelects = Array.from(
      document.querySelectorAll(this.constructor.SELECTOR.regionSelects),
    );
    this.hiddenFields = document.querySelectorAll(
      this.constructor.SELECTOR.hiddenFields,
    );
    this.dateFields = document.querySelectorAll(
      this.constructor.SELECTOR.dateFields,
    );
    this.shortUrl = getPageName();
    this.hasHiddenFields = this.hiddenFields.length > 0;
    this.pageProgram = null;
  }

  async initialize() {
    // Сначала обработчики, потом поля: setupFields() на странице программы сразу
    // проставляет даты и, если поток всего один, триггерит на select "change" для
    // автовыбора — обработчик должен быть уже навешан, иначе cohort-id-hidden
    // останется пустым для единственного потока.
    this.setupEventHandlers();
    this.setupFields();
  }

  setupFields() {
    if (this.hasHiddenFields) {
      this.setupHiddenFields();
    }
    // На главной программа остаётся пустой, пока не выбран регион —
    // ничего дополнительно рендерить не нужно, поле и так пустое по вёрстке.
  }

  setupEventHandlers() {
    if (this.hasHiddenFields) {
      /* Страница программы: свой регион у каждой из форм (основная + hero) */
      this.regionSelects.forEach((select) => {
        select.onchange = this.handleProgramPageRegionChange.bind(this);
      });
    } else {
      /* Главная: список программ строится только после выбора региона */
      this.regionSelects.forEach((select) => {
        select.onchange = this.handleHomeRegionChange.bind(this);
      });

      if (this.programSelect) {
        this.programSelect.onchange = this.handleProgramChange.bind(this);
      }
    }

    this.dateFields.forEach((field) => {
      $(field).on("change", this.handleDateChange.bind(this));
    });
  }

  /* ==================== Главная страница ==================== */

  handleHomeRegionChange(event) {
    const regionId = event.target.value;

    /* Смена региона сбрасывает программу и дату */
    this.resetProgramField();
    this.resetDateField();

    if (!regionId) {
      /* Регион не выбран — поле "Программа" снова неактивно */
      disableProgramField();
      return;
    }

    enableProgramField();
    this.renderProgramOptions(regionId);
  }

  handleProgramChange(event) {
    const regionId = this.regionSelects[0]?.value || null;
    const data = getDataProgram(this.requestPrograms, event, regionId);

    if (!data) return;

    const selectedOption = event.target.options[event.target.selectedIndex];
    this.setProgramAttributes(selectedOption, data);

    /* Смена программы сбрасывает дату */
    this.resetDateField();

    if (!data.shortname) {
      return;
    }

    const program = this.requestPrograms.find(
      (item) => item.shortname === data.shortname,
    );
    const cohorts = this.getProgramCohorts(program, regionId);

    if (cohorts.length) {
      this.appendDateProgramOptions(cohorts);
    } else {
      console.error("Не переданы даты для программы!");
    }
  }

  getProgramCohorts(program, regionId) {
    if (!program) return [];

    const regionData =
      program.regions?.find((region) => region.region_id == regionId) ||
      program.regions?.[0];

    return regionData?.cohorts || [];
  }

  resetProgramField() {
    if (!this.programSelect) return;

    this.clearOptions(this.programSelect);
    $(this.programSelect).val(null).trigger("change");
  }

  resetDateField() {
    this.dateFields.forEach((field) => {
      this.clearOptions(field);
      $(field).val(null).trigger("change");
    });
  }

  /* Заполняет ВСЕ поля дат на странице одним и тем же набором потоков
     (используется там, где на странице ровно один актуальный контекст программы+региона) */
  appendDateProgramOptions(cohorts) {
    this.dateFields.forEach((field) => {
      this.renderDateOptionsInField(field, cohorts);
    });
  }

  /* Заполняет потоками конкретное поле даты, полностью заменяя его текущие опции */
  renderDateOptionsInField(field, cohorts) {
    this.clearOptions(field);
    $(field).val(null).trigger("change");

    cohorts.forEach((cohort) => {
      field.appendChild(this.createDateOption(cohort));
    });

    /* Если поток всего один — выбираем его автоматически,
       если их несколько — пользователь должен выбрать сам. */
    if (cohorts.length === 1) {
      $(field).val(cohorts[0].start_date_text).trigger("change");
    }
  }

  createDateOption(cohort) {
    const option = new Option(cohort.title, cohort.start_date_text, false, false);
    option.setAttribute("data-cohort-id", cohort.cohort_id);
    return option;
  }

  /* Синхронизирует скрытое поле cohort_id этой же формы с выбранным потоком */
  handleDateChange(event) {
    const select = event.target;
    const form = select.closest("form");
    const cohortField = form?.querySelector(this.constructor.SELECTOR.cohortFields);

    if (!cohortField) return;

    const selectedOption = select.options[select.selectedIndex];
    cohortField.value = selectedOption?.getAttribute("data-cohort-id") || "";
  }

  renderProgramOptions(regionId) {
    if (!this.programSelect) return;

    this.clearOptions(this.programSelect);

    const availablePrograms = this.filterPrograms(this.requestPrograms).filter(
      (program) => this.isProgramAvailableInRegion(program, regionId),
    );
    const { withRedirect, withoutRedirect } =
      this.separateProgramsByRedirect(availablePrograms);

    this.appendProgramOptions(withRedirect, true);
    this.appendProgramOptions(withoutRedirect, false);
  }

  isProgramAvailableInRegion(program, regionId) {
    return !!program.regions?.some(
      (region) => region.region_id == regionId,
    );
  }

  clearOptions(element) {
    // Оставляем только первый элемент (placeholder)
    while (element.children.length > 1) {
      element.removeChild(element.lastChild);
    }
  }

  filterPrograms(programs) {
    return programs.filter(
      (program) => !this.constructor.EXCLUDED_PROGRAMS.has(program.shortname),
    );
  }

  separateProgramsByRedirect(programs) {
    return programs.reduce(
      (acc, program) => {
        const hasRedirect = program.regions?.some((region) =>
          region.fields?.some((field) => field.field === "rvr_link"),
        );

        if (hasRedirect) {
          acc.withRedirect.push(program);
        } else {
          acc.withoutRedirect.push(program);
        }

        return acc;
      },
      { withRedirect: [], withoutRedirect: [] },
    );
  }

  appendProgramOptions(programs, insertAtBeginning = false) {
    programs.forEach((program) => {
      const option = this.createProgramOption(program);

      if (insertAtBeginning && this.programSelect.children[1]) {
        this.programSelect.insertBefore(option, this.programSelect.children[1]);
      } else {
        this.programSelect.appendChild(option);
      }
    });
  }

  createProgramOption(program) {
    const option = new Option(program.name, program.name, false, false);
    option.setAttribute("shortname", program.shortname);

    /* program_group_id одинаков для всех регионов, поэтому ставим его сразу,
       не дожидаясь выбора региона (в отличие от operator/program_id) */
    if (program.program_group_id != null) {
      option.setAttribute("program_group_id", program.program_group_id);
    }

    return option;
  }

  setProgramAttributes(element, data) {
    this.constructor.PROGRAM_ATTRIBUTES.forEach((attr) => {
      if (data[attr] != null) {
        element.setAttribute(attr, data[attr]);
      } else {
        element.removeAttribute(attr);
      }
    });
  }

  /* ==================== Страница программы ==================== */

  getPageProgram() {
    return this.requestPrograms.find(
      (program) => program.shortname === this.shortUrl,
    );
  }

  setupHiddenFields() {
    const pageProgram = this.getPageProgram();
    this.pageProgram = pageProgram;

    this.hiddenFields.forEach((field) => {
      this.setupHiddenField(field, pageProgram);
    });

    /* Регион ещё не выбран — берём потоки первого доступного региона программы,
       handleProgramPageRegionChange() пересчитает их точнее при выборе региона */
    const initialRegionId = this.regionSelects[0]?.value || null;
    const cohorts = this.getProgramCohorts(pageProgram, initialRegionId);

    if (cohorts.length) {
      this.appendDateProgramOptions(cohorts);
    } else {
      console.error("Не переданы даты для программы!");
    }
  }

  setupHiddenField(field, pageProgram) {
    field.setAttribute("shortname", this.shortUrl);

    if (pageProgram?.program_group_id != null) {
      field.setAttribute("program_group_id", pageProgram.program_group_id);
    }

    if (pageProgram?.name) {
      field.value = pageProgram.name;
    } else {
      // Фолбэк на случай отсутствия программы в API — берём название из заголовка страницы
      const title = document.querySelector(".home__title")?.textContent;

      if (title) {
        const normalized = title.replace(/ /g, " ");
        const extracted = normalized.slice(
          normalized.indexOf("«") + 1,
          normalized.indexOf("»"),
        );

        if (extracted) {
          field.value = extracted;
        }
      }
    }

    // program_id/operator зависят от региона — появятся после его выбора
  }

  handleProgramPageRegionChange(event) {
    const regionSelect = event.target;
    const form = regionSelect.closest("form");
    const hiddenField = form?.querySelector(
      this.constructor.SELECTOR.hiddenFields,
    );

    if (!hiddenField) return;

    const regionId = regionSelect.value;
    const data = getDataProgram(this.requestPrograms, hiddenField, regionId);

    this.setProgramAttributes(hiddenField, data);

    /* Обновляем даты потоков только в поле этой же формы — под выбранный регион */
    const dateField = form?.querySelector(this.constructor.SELECTOR.dateFields);

    if (!dateField) return;

    const cohorts = this.getProgramCohorts(this.pageProgram, regionId);

    this.renderDateOptionsInField(dateField, cohorts);

    if (!cohorts.length) {
      console.error("Не переданы даты для программы!");
    }
  }
}

async function initDropDownsPrograms(requestPrograms) {
  const manager = new ProgramDropdownManager(requestPrograms);
  await manager.initialize();
  return manager;
}

export default initDropDownsPrograms;
